import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Wallet } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { formatCurrency } from "@/utils/currency";

interface Props {
  /** The whole balance, as it would be charged today. */
  amount?: number | null;
  checked: boolean;
  onChange: (payInFull: boolean) => void;
  disabled?: boolean;
}

/**
 * The way out of the schedule, on the plans that offer one: settle the whole
 * balance today in a single payment.
 *
 * Only rendered for a plan whose `is_pay_in_full_allowed` is set — the API
 * refuses it for any other plan, so offering it there would promise something
 * checkout would not do.
 */
const PayInFullChoice: FC<Props> = ({ amount, checked, onChange, disabled }) => {
  const { t, i18n } = useTranslation();

  return (
    <section className="mt-6 rounded-2xl border border-muted-foreground/20 bg-muted/30 p-5">
      <div className="flex items-start gap-3">
        <Checkbox
          checked={checked}
          className="mt-0.5"
          data-testid="pay-in-full"
          disabled={disabled}
          id="pay-in-full"
          onCheckedChange={(value) => onChange(value === true)}
        />
        <label className="cursor-pointer" htmlFor="pay-in-full">
          <span className="flex items-center gap-2 text-base font-semibold text-foreground">
            <Wallet size={18} className="text-primary" />
            {amount
              ? t("fe.pages.flow.steps.paymentMethod.payInFullWithAmount", {
                  amount: formatCurrency(amount, i18n.language),
                  defaultValue: "Pay everything now — {{amount}}",
                })
              : t("fe.pages.flow.steps.paymentMethod.payInFull", {
                  defaultValue: "Pay everything now",
                })}
          </span>
          <span className="mt-1 block text-sm leading-5 text-muted-foreground">
            {t("fe.pages.flow.steps.paymentMethod.payInFullHint", {
              defaultValue:
                "Settles the balance in one payment today instead of the instalments below.",
            })}
          </span>
        </label>
      </div>
    </section>
  );
};

export default PayInFullChoice;
